import React, { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { UserListItem } from "interface/user";
import { ICON_NAMES } from "utils/constant";

interface UserListMobileListProps {
	items: UserListItem[];
	loading: boolean;
	onEdit: (item: UserListItem) => void;
	onDelete: (item: UserListItem) => void;
	onLoadMore: () => void;
}

const splitMultiValue = (value?: string | null) => {
	if (!value) {
		return [];
	}

	return value
		.split("<br/>")
		.map((item) => item.trim())
		.filter(Boolean);
};

const getFullName = (item: UserListItem) => {
	const parts = [item.firstname, item.lastname]
		.map((part) => (part || "").trim())
		.filter(Boolean);

	if (parts.length > 0) {
		return parts.join(" ");
	}

	return item.fullname || item.email || "-";
};

const getInitials = (item: UserListItem) => {
	if (item.initials?.trim()) {
		return item.initials.trim();
	}

	const parts = [item.firstname, item.lastname]
		.map((part) => (part || "").trim())
		.filter(Boolean);

	if (parts.length === 0) {
		return "-";
	}

	return parts.map((part) => part[0]).join("").toUpperCase();
};

const renderValues = (values: string[]) => {
	if (values.length === 0) {
		return <strong>-</strong>;
	}

	return (
		<div className="user-list-card-tags">
			{values.map((value, index) => (
				<span key={`${value}-${index}`} className="user-list-card-tag">
					{value}
				</span>
			))}
		</div>
	);
};

const UserListMobileList: FC<UserListMobileListProps> = ({
	items,
	loading,
	onEdit,
	onDelete,
	onLoadMore,
}) => {
	const { t } = useTranslation();
	const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

	const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
		const target = event.currentTarget;
		const remaining = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (remaining < 200) {
			onLoadMore();
		}
	};

	return (
		<div className="user-list-mobile" onScroll={handleScroll}>
			{empty && <div className="user-list-mobile-empty">{t("NoRows")}</div>}

			{items.map((item, itemIndex) => {
				const buyers = splitMultiValue(item.buyers);
				const vessels = splitMultiValue(item.vessels);
				const terminals = splitMultiValue(item.terminals);
				const roles = splitMultiValue(item.roles);
				const itemKey = `${item.id || "nouser"}-${item.email || itemIndex}-${itemIndex}`;

				return (
					<div key={itemKey} className="user-list-card">
						<div className="user-list-card-header">
							<div className="user-list-card-initials">{getInitials(item)}</div>
							<div className="user-list-card-name">{getFullName(item)}</div>
							<div className="user-list-card-email">{item.email || "-"}</div>
						</div>

						<div className="user-list-card-body">
							<div className="user-list-card-body-full">
								<span>{t("Roles")}</span>
								{renderValues(roles)}
							</div>
							<div className="user-list-card-body-full">
								<span>{t("Buyer")}</span>
								{renderValues(buyers)}
							</div>
							<div className="user-list-card-body-full">
								<span>{t("Vessel")}</span>
								{renderValues(vessels)}
							</div>
							<div className="user-list-card-body-full">
								<span>{t("Terminal")}</span>
								{renderValues(terminals)}
							</div>
						</div>

						<div className="user-list-card-actions">
							<button
								type="button"
								className="user-list-icon-btn"
								onClick={() => onEdit(item)}
								title={t("Edit")}
							>
								<img src={`/icon/${ICON_NAMES.Edit}.svg`} alt={t("Edit")} />
							</button>
							<button
								type="button"
								className="user-list-icon-btn"
								onClick={() => onDelete(item)}
								title={t("Delete")}
							>
								<img src={`/icon/${ICON_NAMES.Delete}.svg`} alt={t("Delete")} />
							</button>
						</div>
					</div>
				);
			})}

			{loading && <div className="user-list-mobile-loading">{t("Loading")}</div>}
		</div>
	);
};

export default UserListMobileList;